(function bootstrapEssencraftIntelligentReviews(global) {
  'use strict';

  if (global.ECIntelligentReviews && typeof global.ECIntelligentReviews.initAll === 'function') {
    global.ECIntelligentReviews.initAll(document);
    return;
  }

  const ROOT_SELECTOR = '[data-ec-intelligent-reviews]';
  const THEMES = [
    ['all', 'All experiences'],
    ['energy-focus', 'Energy & focus'],
    ['strength-performance', 'Strength & performance'],
    ['routine-fit', 'Routine fit'],
    ['quality-value', 'Quality & value'],
    ['taste-texture', 'Taste & texture'],
    ['overall-experience', 'Overall experience']
  ];

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);

  function formatDate(value) {
    if (!value) return 'Date unavailable';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }

  function starText(rating) {
    const rounded = clamp(Math.round(Number(rating) || 0), 0, 5);
    return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
  }

  function themeLabel(tag) {
    const entry = THEMES.find(([key]) => key === tag);
    return entry ? entry[1] : 'Customer experience';
  }

  function parseIds(value) {
    return new Set(
      String(value || '')
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  function isValidAmazonReviewUrl(value, reviewId, asin) {
    try {
      const url = new URL(value);
      return (
        url.protocol === 'https:' &&
        url.hostname === 'www.amazon.com' &&
        url.pathname === `/gp/customer-reviews/${reviewId}` &&
        url.searchParams.get('ASIN') === asin
      );
    } catch (error) {
      return false;
    }
  }

  function emitEvent(name, detail) {
    global.dataLayer = global.dataLayer || [];
    global.dataLayer.push({ event: name, ...detail });
  }

  function makeElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  class IntelligentReviewSection {
    constructor(root, payload) {
      this.root = root;
      this.payload = payload;
      this.productKey = root.dataset.product;
      this.productName = root.dataset.productName;
      this.asin = root.dataset.asin;
      this.displayRating = clamp(Number(root.dataset.rating) || 0, 1, 5);
      this.sourceCount = Math.max(0, Number(String(root.dataset.sourceCount || '').replace(/,/g, '')) || 0);
      this.sourceUrl = root.dataset.sourceUrl;
      this.pageSize = clamp(Number(root.dataset.pageSize) || 10, 5, 20);
      this.hiddenIds = parseIds(root.dataset.hiddenReviewIds);

      this.state = {
        featureId: root.dataset.featuredReviewId || null,
        theme: 'all',
        query: '',
        sort: 'smart',
        limit: this.pageSize
      };
      this.lastFocus = null;
      this.abortController = new AbortController();

      this.cacheElements();
      this.reviews = this.getValidatedReviews();
      this.bindEvents();
      this.renderAggregate();
      this.renderMain();
      this.root.dataset.ecIrwReady = 'true';
    }

    cacheElements() {
      const find = (selector) => this.root.querySelector(selector);
      this.elements = {
        aggregateLink: find('[data-ec-irw-aggregate-link]'),
        aggregateRating: find('[data-ec-irw-rating]'),
        aggregateStars: find('.ec-irw__aggregate-stars'),
        sourceCount: find('[data-ec-irw-source-count]'),
        reviewTheme: find('[data-ec-irw-theme]'),
        reviewTitle: find('[data-ec-irw-review-title]'),
        reviewBody: find('[data-ec-irw-review-body]'),
        reviewStars: find('[data-ec-irw-review-stars]'),
        verified: find('[data-ec-irw-verified]'),
        reviewer: find('[data-ec-irw-reviewer]'),
        productName: find('[data-ec-irw-product-name]'),
        reviewDate: find('[data-ec-irw-review-date]'),
        reviewSource: find('[data-ec-irw-review-source]'),
        readReview: find('[data-ec-irw-read-review]'),
        previous: find('[data-ec-irw-previous]'),
        next: find('[data-ec-irw-next]'),
        position: find('[data-ec-irw-position]'),
        viewableCount: find('[data-ec-irw-viewable-count]'),
        openLibrary: find('[data-ec-irw-open-library]'),
        dialog: find('[data-ec-irw-library]'),
        closeLibrary: find('[data-ec-irw-close-library]'),
        reconciliation: find('[data-ec-irw-reconciliation]'),
        search: find('[data-ec-irw-search]'),
        sort: find('[data-ec-irw-sort]'),
        topics: find('[data-ec-irw-topics]'),
        resultsHeading: find('[data-ec-irw-results-heading]'),
        resultsCount: find('[data-ec-irw-results-count]'),
        results: find('[data-ec-irw-results]'),
        empty: find('[data-ec-irw-empty]'),
        loadMore: find('[data-ec-irw-load-more]'),
        status: find('[data-ec-irw-status]')
      };
    }

    getValidatedReviews() {
      if (!this.payload || !Array.isArray(this.payload.reviews)) return [];
      return this.payload.reviews.filter((review) => {
        if (!review || review.product !== this.productKey || review.asin !== this.asin) return false;
        if (this.hiddenIds.has(review.id)) return false;
        if (!review.title || !review.body || !review.id) return false;
        return isValidAmazonReviewUrl(review.sourceUrl, review.id, this.asin);
      });
    }

    reviewerName(review) {
      const value = String(review.reviewer || '').trim();
      if (!value || value.length > 64) return 'Amazon customer';
      return value;
    }

    bindEvents() {
      const signal = this.abortController.signal;
      this.elements.previous.addEventListener('click', () => this.cycleFeature(-1), { signal });
      this.elements.next.addEventListener('click', () => this.cycleFeature(1), { signal });
      this.elements.openLibrary.addEventListener('click', () => this.openLibrary(false), { signal });
      this.elements.readReview.addEventListener('click', () => this.openLibrary(true), { signal });
      this.elements.closeLibrary.addEventListener('click', () => this.closeLibrary(), { signal });
      this.elements.search.addEventListener(
        'input',
        (event) => {
          this.state.query = event.currentTarget.value;
          this.state.limit = this.pageSize;
          this.renderLibrary();
        },
        { signal }
      );
      this.elements.sort.addEventListener(
        'change',
        (event) => {
          this.state.sort = event.currentTarget.value;
          this.state.limit = this.pageSize;
          this.renderLibrary();
          emitEvent('ec_review_sort_selected', {
            ec_product: this.productKey,
            ec_review_sort: this.state.sort
          });
        },
        { signal }
      );
      this.elements.loadMore.addEventListener(
        'click',
        () => {
          this.state.limit += this.pageSize;
          this.renderLibrary();
          emitEvent('ec_review_library_page_loaded', {
            ec_product: this.productKey,
            ec_review_limit: this.state.limit
          });
        },
        { signal }
      );
      this.elements.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.elements.dialog) this.closeLibrary();
        },
        { signal }
      );
      this.elements.dialog.addEventListener(
        'close',
        () => {
          document.body.classList.remove('ec-irw-is-locked');
          if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
        },
        { signal }
      );
      this.elements.aggregateLink.addEventListener(
        'click',
        () => {
          emitEvent('ec_review_source_opened', { ec_product: this.productKey });
        },
        { signal }
      );
    }

    smartScore(review) {
      let score = 0;
      if (Number.isFinite(review.featuredRank)) score += 1000 - review.featuredRank * 50;
      if (review.verified) score += 25;
      score += Math.min(Number(review.helpful) || 0, 100) * 2;
      score += Math.min(review.body.length, 800) / 80;
      if (review.date) {
        const timestamp = new Date(`${review.date}T00:00:00`).getTime();
        if (Number.isFinite(timestamp)) score += timestamp / 1e13;
      }
      return score;
    }

    sortReviews(reviews, sort = this.state.sort) {
      const result = [...reviews];
      if (sort === 'recent') {
        return result.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
      }
      if (sort === 'helpful') {
        return result.sort(
          (a, b) => (Number(b.helpful) || 0) - (Number(a.helpful) || 0) || this.smartScore(b) - this.smartScore(a)
        );
      }
      if (sort === 'rating') {
        return result.sort((a, b) => Number(b.rating) - Number(a.rating) || this.smartScore(b) - this.smartScore(a));
      }
      return result.sort((a, b) => this.smartScore(b) - this.smartScore(a));
    }

    featuredReviews() {
      return this.sortReviews(this.reviews, 'smart');
    }

    currentReview() {
      const reviews = this.featuredReviews();
      return reviews.find((review) => review.id === this.state.featureId) || reviews[0] || null;
    }

    renderAggregate() {
      this.elements.aggregateRating.textContent = this.displayRating.toFixed(1);
      this.elements.sourceCount.textContent = formatNumber(this.sourceCount);
      this.elements.aggregateStars.style.setProperty('--ec-irw-rating-fill', `${this.displayRating * 20}%`);
      this.elements.aggregateLink.setAttribute(
        'aria-label',
        `${this.displayRating.toFixed(1)} out of 5 from ${formatNumber(this.sourceCount)} Amazon ratings for ${this.productName}`
      );
    }

    renderMain() {
      const reviews = this.featuredReviews();
      if (!reviews.some((review) => review.id === this.state.featureId)) {
        this.state.featureId = reviews[0] ? reviews[0].id : null;
      }

      this.elements.viewableCount.textContent = formatNumber(reviews.length);
      this.elements.openLibrary.disabled = reviews.length === 0;
      this.elements.readReview.disabled = reviews.length === 0;
      this.elements.previous.disabled = reviews.length < 2;
      this.elements.next.disabled = reviews.length < 2;

      const review = this.currentReview();
      if (!review) {
        this.elements.status.textContent = 'The selected review library is unavailable.';
        return;
      }
      this.renderFeature(review, false);
    }

    renderFeature(review, announce) {
      this.state.featureId = review.id;
      this.elements.reviewTheme.textContent = themeLabel(review.tags[0]);
      this.elements.reviewTitle.textContent = review.title;
      this.elements.reviewBody.textContent = review.body;
      this.elements.reviewStars.textContent = starText(review.rating);
      this.elements.reviewStars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      this.elements.verified.hidden = !review.verified;
      this.elements.reviewer.textContent = this.reviewerName(review);
      this.elements.productName.textContent = this.productName;
      this.elements.reviewDate.textContent = formatDate(review.date);
      this.elements.reviewSource.href = review.sourceUrl;

      const reviews = this.featuredReviews();
      const position = reviews.findIndex((candidate) => candidate.id === review.id) + 1;
      this.elements.position.textContent = `Review ${position} of ${reviews.length}`;
      if (announce) {
        this.elements.status.textContent = `Showing review ${position} of ${reviews.length}: ${review.title}`;
        emitEvent('ec_review_story_selected', {
          ec_product: this.productKey,
          ec_review_id: review.id
        });
      }
    }

    cycleFeature(direction) {
      const reviews = this.featuredReviews();
      if (reviews.length < 2) return;
      const currentIndex = Math.max(0, reviews.findIndex((review) => review.id === this.state.featureId));
      const nextIndex = (currentIndex + direction + reviews.length) % reviews.length;
      this.renderFeature(reviews[nextIndex], true);
    }

    libraryReviews() {
      const query = this.state.query.trim().toLowerCase();
      const filtered = this.reviews.filter((review) => {
        const topicMatch = this.state.theme === 'all' || review.tags.includes(this.state.theme);
        const queryMatch =
          !query ||
          `${review.title} ${review.body} ${this.reviewerName(review)}`.toLowerCase().includes(query);
        return topicMatch && queryMatch;
      });
      const sorted = this.sortReviews(filtered);
      if (!query && this.state.theme === 'all' && this.state.featureId) {
        sorted.sort((a, b) => (a.id === this.state.featureId ? -1 : b.id === this.state.featureId ? 1 : 0));
      }
      return sorted;
    }

    renderTopics() {
      const holder = this.elements.topics;
      holder.replaceChildren();
      THEMES.forEach(([key, label]) => {
        const count = key === 'all' ? this.reviews.length : this.reviews.filter((review) => review.tags.includes(key)).length;
        if (key !== 'all' && count === 0) return;
        const button = makeElement('button', '', `${label} · ${formatNumber(count)}`);
        button.type = 'button';
        button.dataset.theme = key;
        button.setAttribute('aria-pressed', String(this.state.theme === key));
        button.addEventListener('click', () => {
          this.state.theme = key;
          this.state.limit = this.pageSize;
          this.renderLibrary();
          emitEvent('ec_review_filter_selected', {
            ec_product: this.productKey,
            ec_review_theme: key
          });
        });
        holder.append(button);
      });
    }

    createReviewElement(review) {
      const article = makeElement('article', 'ec-irw-review');
      article.dataset.reviewId = review.id;

      const header = makeElement('header');
      const rating = makeElement('div', 'ec-irw-review__rating');
      const stars = makeElement('span', 'ec-irw-review__stars', starText(review.rating));
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      rating.append(stars);
      if (review.verified) rating.append(makeElement('span', 'ec-irw-review__verified', 'Verified purchase'));
      const time = makeElement('time', '', formatDate(review.date));
      if (review.date) time.dateTime = review.date;
      header.append(rating, time);

      const title = makeElement('h4', '', review.title);
      const body = makeElement('p', 'ec-irw-review__body', review.body);

      const footer = makeElement('footer');
      footer.append(makeElement('p', '', `${this.reviewerName(review)} · ${this.productName}`));
      const source = makeElement('a', '', 'View original Amazon review ↗');
      source.href = review.sourceUrl;
      source.target = '_blank';
      source.rel = 'noopener noreferrer';
      source.addEventListener('click', () => {
        emitEvent('ec_review_source_opened', {
          ec_product: this.productKey,
          ec_review_id: review.id
        });
      });
      footer.append(source);

      article.append(header, title, body, footer);
      return article;
    }

    renderLibrary() {
      const reviews = this.libraryReviews();
      const visible = reviews.slice(0, this.state.limit);
      this.renderTopics();
      this.elements.results.replaceChildren(...visible.map((review) => this.createReviewElement(review)));
      this.elements.empty.hidden = reviews.length !== 0;
      this.elements.loadMore.hidden = visible.length >= reviews.length;
      this.elements.resultsCount.textContent = `Showing ${formatNumber(visible.length)} of ${formatNumber(reviews.length)}`;
      this.elements.resultsHeading.textContent =
        this.state.theme === 'all' ? `Selected ${this.productName} reviews` : themeLabel(this.state.theme);
      this.elements.reconciliation.textContent = `${formatNumber(this.reviews.length)} selected reviews are available here. Amazon’s ${formatNumber(this.sourceCount)} public ratings are a separate source total.`;
    }

    openLibrary(fromFeature) {
      if (!this.reviews.length) return;
      this.lastFocus = document.activeElement;
      if (fromFeature) {
        this.state.theme = 'all';
        this.state.query = '';
        this.state.sort = 'smart';
        this.elements.search.value = '';
        this.elements.sort.value = 'smart';
      } else {
        this.state.featureId = this.currentReview().id;
      }
      this.state.limit = this.pageSize;
      this.renderLibrary();
      document.body.classList.add('ec-irw-is-locked');
      if (typeof this.elements.dialog.showModal === 'function') {
        this.elements.dialog.showModal();
      } else {
        this.elements.dialog.setAttribute('open', '');
      }
      requestAnimationFrame(() => this.elements.search.focus());
      emitEvent('ec_review_library_opened', {
        ec_product: this.productKey,
        ec_review_id: this.state.featureId
      });
    }

    closeLibrary() {
      if (typeof this.elements.dialog.close === 'function' && this.elements.dialog.open) {
        this.elements.dialog.close();
      } else {
        this.elements.dialog.removeAttribute('open');
        document.body.classList.remove('ec-irw-is-locked');
        if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
      }
    }

    destroy() {
      this.closeLibrary();
      this.abortController.abort();
      delete this.root.dataset.ecIrwReady;
    }
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement) || root.dataset.ecIrwReady === 'true') return;
    const payload = global.ECIntelligentReviewsData;
    if (!payload || !payload.products || !payload.products[root.dataset.product]) {
      const status = root.querySelector('[data-ec-irw-status]');
      if (status) status.textContent = 'Customer reviews could not be loaded.';
      return;
    }
    root.ecIntelligentReviews = new IntelligentReviewSection(root, payload);
  }

  function initAll(scope) {
    const rootScope = scope || document;
    if (rootScope.matches && rootScope.matches(ROOT_SELECTOR)) initRoot(rootScope);
    rootScope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  global.ECIntelligentReviews = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
    if (root && root.ecIntelligentReviews) root.ecIntelligentReviews.destroy();
  });
})(window);
